import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  Checkbox,
  DateInput,
  DateInputSize,
  IconLoader,
  InputSize,
  InputText,
  SelectSize,
} from '@ui/src';
import { NewApplicationStatus } from '../../../../hoc/common/types';
import InfoRow from '../../common/info-row';
import { SecondPassportTypes } from './types';

import commonStyles from '../../common/styles.module.css';
import cn from '../styles.module.css';

const SecondPassport = ({
  haveSecondPassport,
  setHaveSecondPassport,
  isEditable,
  status,
  questionnarieFields,
  questionnarieErrors,
  questionnarieControl,
  questionnarieClearErrors,
  countries,
  questionnarieIsLoading,
}: SecondPassportTypes) => (
  <>
    <div className={cn.cddBlockTitle}>2nd Passport</div>

    <div className={cn.cddBlockContentWrapper}>
      <div className={cn.cddBlockContent}>
        {questionnarieIsLoading && (
          <div className={commonStyles.dictionaryLoader}>
            <IconLoader />
          </div>
        )}
        {!questionnarieIsLoading && (
          <>
            <div
              className={`${commonStyles.addressCheckboxWrapper} ${commonStyles.stepOneCheckboxWrapper} ${commonStyles.cddBlockContentCheckbox}`}
            >
              <Checkbox
                name="haveSecondPassport"
                type="checkbox"
                checked={haveSecondPassport}
                onChange={(e) => setHaveSecondPassport(e.target.checked)}
                title="Having a second passport"
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  ) ||
                  Boolean(
                    questionnarieFields?.otherNationalitiesIds &&
                    (questionnarieErrors?.secondPassportIssueCountryId ||
                      questionnarieErrors?.secondPassportIssueDate ||
                      questionnarieErrors?.secondPassportNumber),
                  )
                }
                tabIndex={
                  !isEditable &&
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                    ? -1
                    : 0
                }
                labelClassName={cn.addressCheckbox}
              />
            </div>
            {haveSecondPassport && (
              <>
                <InfoRow
                  title="ID / Passport number"
                  id="secondPassportNumber"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="secondPassportNumber"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <InputText
                        name="secondPassportNumber"
                        placeholder="Enter first name..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['secondPassportNumber']);
                        }}
                        size={InputSize.SMALL}
                        className="borderless"
                        error={
                          questionnarieErrors?.secondPassportNumber?.message
                        }
                        tabIndex={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
                <InfoRow
                  title="ID / Passport issue country"
                  id="secondPassportIssueCountryId"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="secondPassportIssueCountryId"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Autocomplete
                        name={name}
                        placeholder="Select country..."
                        list={countries}
                        value={value}
                        size={SelectSize.SMALL}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors([
                            'secondPassportIssueCountryId',
                          ]);
                        }}
                        selectWrapperClassName={
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED
                            ? commonStyles.selectDisabled
                            : ''
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        error={
                          questionnarieErrors?.secondPassportIssueCountryId
                            ?.message
                        }
                        fullWidth
                        borderless
                      />
                    )}
                  />
                </InfoRow>
                <InfoRow
                  title="ID / Passport issue date"
                  id="secondPassportIssueDate"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="secondPassportIssueDate"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => {
                      let className;

                      if (
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                      ) {
                        className = commonStyles.dateInputDisabled;

                        if (value) {
                          className = `${commonStyles.dateInputDisabled} ${
                            value ? commonStyles.dateInputDisabledWithValue : ''
                          }`;
                        }
                      }
                      return (
                        <DateInput
                          value={value || ''}
                          placeholder="Enter date..."
                          onChange={(date) => {
                            onChange(date.toISOString());
                            questionnarieClearErrors([
                              'secondPassportIssueDate',
                            ]);
                          }}
                          disabledAfter={new Date()}
                          className={className}
                          size={DateInputSize.SMALL}
                          error={
                            questionnarieErrors?.secondPassportIssueDate
                              ?.message
                          }
                          tabIndex={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          borderless
                          withoutCustomSelector
                        />
                      );
                    }}
                  />
                </InfoRow>
                <InfoRow
                  title="ID / Passport expiry date"
                  id="secondPassportExpiryDate"
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="secondPassportExpiryDate"
                    defaultValue=""
                    render={({ field: { onChange, value } }) => {
                      let className;

                      if (
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                      ) {
                        className = commonStyles.dateInputDisabled;

                        if (value) {
                          className = `${commonStyles.dateInputDisabled} ${
                            value ? commonStyles.dateInputDisabledWithValue : ''
                          }`;
                        }
                      }
                      return (
                        <DateInput
                          value={value || ''}
                          placeholder="Enter date..."
                          onChange={(date) => {
                            onChange(date.toISOString());
                            questionnarieClearErrors([
                              'secondPassportExpiryDate',
                            ]);
                          }}
                          className={className}
                          size={DateInputSize.SMALL}
                          error={
                            questionnarieErrors?.secondPassportExpiryDate
                              ?.message
                          }
                          tabIndex={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                              ? -1
                              : 0
                          }
                          disabled={
                            !isEditable ||
                            Boolean(
                              status === NewApplicationStatus.ACCEPTED ||
                              status === NewApplicationStatus.DECLINED,
                            )
                          }
                          borderless
                          withoutCustomSelector
                        />
                      );
                    }}
                  />
                </InfoRow>
              </>
            )}
          </>
        )}
      </div>
    </div>
  </>
);

export default SecondPassport;
